import Phaser from "phaser";
import { register, login, setToken } from "../api";
import { FONT, UI } from "../types";

/**
 * El login es una escena limpia (sin marco de madera) para que el form
 * HTML overlay quede bien visible y centrado.
 */
export class AuthScene extends Phaser.Scene {
  private overlay: HTMLDivElement | null = null;

  constructor() {
    super({ key: "AuthScene" });
  }

  create(): void {
    const { width, height } = this.scale;

    // Fondo sólido oscuro
    this.add.rectangle(width / 2, height / 2, width, height, UI.BG);

    this.add.text(width / 2, 100, "ImperiumGuardians", {
      fontFamily: FONT, fontSize: "24px", fontStyle: "bold", color: "#f5c242",
    }).setOrigin(0.5);

    this.add.text(width / 2, 128, "Sidegame de ImperiumAO", {
      fontFamily: FONT, fontSize: "11px", color: "#aaa",
    }).setOrigin(0.5);

    this.createHTMLOverlay();

    // Limpiar overlay al salir de la escena
    this.events.on("shutdown", () => this.removeOverlay());
    this.events.on("destroy", () => this.removeOverlay());
  }

  private createHTMLOverlay(): void {
    // Buscar el contenedor del canvas de Phaser
    const canvas = this.game.canvas;
    const parent = canvas.parentElement;
    if (!parent) return;

    // Crear overlay posicionado encima del canvas
    this.overlay = document.createElement("div");
    this.overlay.id = "auth-overlay";
    this.overlay.innerHTML = `
      <div style="
        width: 260px;
        box-sizing: border-box;
        font-family: Nunito, sans-serif;
        display: flex; flex-direction: column; gap: 10px;
        padding: 20px;
        background: #3e2f1c;
        border-radius: 12px;
        border: 2px solid #5a4230;
      ">
        <input id="auth-username" type="text" placeholder="Nombre de usuario"
          style="display:none; box-sizing:border-box; width:100%; padding:10px; border-radius:8px; border:1px solid #5a4230; background:#2d2213; color:#f0e6d0; font-family:Nunito; font-size:14px; outline:none;" />
        <input id="auth-email" type="text" placeholder="Email o usuario"
          style="box-sizing:border-box; width:100%; padding:10px; border-radius:8px; border:1px solid #5a4230; background:#2d2213; color:#f0e6d0; font-family:Nunito; font-size:14px; outline:none;" />
        <input id="auth-password" type="password" placeholder="Contraseña"
          style="box-sizing:border-box; width:100%; padding:10px; border-radius:8px; border:1px solid #5a4230; background:#2d2213; color:#f0e6d0; font-family:Nunito; font-size:14px; outline:none;" />
        <button id="auth-submit"
          style="box-sizing:border-box; width:100%; padding:12px; border-radius:8px; border:none; background:#f5c242; color:#2d2213; font-family:Nunito; font-size:16px; font-weight:bold; cursor:pointer;">
          Iniciar Sesión
        </button>
        <p id="auth-toggle"
          style="color:#f5c242; text-align:center; cursor:pointer; margin:4px 0 0 0; font-size:13px; font-family:Nunito;">
          ¿No tenés cuenta? Registrate
        </p>
        <p id="auth-error"
          style="color:#e74c3c; text-align:center; margin:4px 0 0 0; font-size:12px; min-height:16px; font-family:Nunito;">
        </p>
      </div>
    `;

    // El overlay se posiciona absoluto dentro del parent del canvas,
    // usando las mismas dimensiones y posición que el canvas.
    Object.assign(this.overlay.style, {
      position: "absolute",
      top: canvas.style.top || "0",
      left: canvas.style.left || "0",
      width: canvas.style.width || `${canvas.width}px`,
      height: canvas.style.height || `${canvas.height}px`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "auto",
      zIndex: "10",
      marginLeft: canvas.style.marginLeft || "0",
      marginTop: canvas.style.marginTop || "0",
    });

    parent.style.position = "relative";
    parent.appendChild(this.overlay);

    // Actualizar posición del overlay si la ventana cambia de tamaño
    const syncPosition = () => {
      if (!this.overlay) return;
      Object.assign(this.overlay.style, {
        top: canvas.style.top || "0",
        left: canvas.style.left || "0",
        width: canvas.style.width || `${canvas.width}px`,
        height: canvas.style.height || `${canvas.height}px`,
        marginLeft: canvas.style.marginLeft || "0",
        marginTop: canvas.style.marginTop || "0",
      });
    };
    this.scale.on("resize", syncPosition);
    // Sync once after Phaser finishes its layout
    this.time.delayedCall(50, syncPosition);

    this.setupFormEvents();
  }

  private setupFormEvents(): void {
    if (!this.overlay) return;

    let isLogin = true;
    const usernameEl = this.overlay.querySelector("#auth-username") as HTMLInputElement;
    const emailEl = this.overlay.querySelector("#auth-email") as HTMLInputElement;
    const passwordEl = this.overlay.querySelector("#auth-password") as HTMLInputElement;
    const submitEl = this.overlay.querySelector("#auth-submit") as HTMLButtonElement;
    const toggleEl = this.overlay.querySelector("#auth-toggle") as HTMLParagraphElement;
    const errorEl = this.overlay.querySelector("#auth-error") as HTMLParagraphElement;

    toggleEl.addEventListener("click", () => {
      isLogin = !isLogin;
      usernameEl.style.display = isLogin ? "none" : "block";
      emailEl.placeholder = isLogin ? "Email o usuario" : "Email";
      emailEl.type = isLogin ? "text" : "email";
      submitEl.textContent = isLogin ? "Iniciar Sesión" : "Registrarse";
      toggleEl.textContent = isLogin
        ? "¿No tenés cuenta? Registrate"
        : "¿Ya tenés cuenta? Iniciá sesión";
      errorEl.textContent = "";
    });

    submitEl.addEventListener("click", async () => {
      errorEl.textContent = "";
      submitEl.disabled = true;
      submitEl.textContent = "Cargando...";
      try {
        const res = isLogin
          ? await login(emailEl.value, passwordEl.value)
          : await register(usernameEl.value, emailEl.value, passwordEl.value);
        setToken(res.token);
        if (res.has_chosen_creatures) {
          this.scene.start("MapScene");
        } else {
          this.scene.start("IntroScene");
        }
      } catch (err) {
        errorEl.textContent = err instanceof Error ? err.message : "Error desconocido";
        submitEl.disabled = false;
        submitEl.textContent = isLogin ? "Iniciar Sesión" : "Registrarse";
      }
    });
  }

  private removeOverlay(): void {
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
  }
}
